import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export default function HeroQuickContact() {
  const [formData, setFormData] = useState({
    navn: "",
    telefon: "",
    email: "",
    beskrivelse: "",
    website_url: "", // Honeypot
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const [errors, setErrors] = useState({});

  // Roterende anmeldelser til tillidsopbygning
  const FALLBACK_REVIEWS = [
    {
      author: "Haftor",
      source: "Google Anmeldelse",
      text: "Jeg har samarbejdet med ETDanmark flere gange i løbet af de sidste tre måneder og har haft en rigtig god oplevelse hver gang.",
      rating: 5,
      date: "7 måneder siden",
    },
    {
      author: "Søren",
      source: "Google Anmeldelse",
      text: "God og ordentlig service til attraktive priser, samt en venlig kommunikation.",
      rating: 5,
      date: "1 måned siden",
    },
    {
      author: "Verificeret kunde",
      source: "Trustpilot",
      text: "Lars leverede en ordentligt og professionel service til en særdeles attraktiv pris, samt en god kommunikation før og under arbejdet.",
      rating: 5,
      date: "1 måned siden",
    },
    {
      author: "Verificeret kunde",
      source: "Trustpilot",
      text: "Super service meget professionel. Ingen ventetid.",
      rating: 5,
      date: "3 uger siden",
    },
    {
      author: "Verificeret kunde",
      source: "Trustpilot",
      text: "Vi havde en god oplevelse med etdanmark. Lars var professionel, struktureret, og nem at kommunikere med. Klar anbefaling!",
      rating: 5,
      date: "7 måneder siden",
    },
  ];

  const [reviewsList, setReviewsList] = useState(FALLBACK_REVIEWS);
  const [activeReviewIdx, setActiveReviewIdx] = useState(0);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    async function loadReviews() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/content/reviews");
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setReviewsList(data);
          }
        }
      } catch (e) {
        // Bevar fallback anmeldelser
      }
    }
    loadReviews();
  }, []);

  useEffect(() => {
    if (reviewsList.length <= 1) return;
    const timer = setInterval(() => {
      setIsFading(true);
      setTimeout(() => {
        setActiveReviewIdx((prev) => (prev + 1) % reviewsList.length);
        setIsFading(false);
      }, 250);
    }, 4500);

    return () => clearInterval(timer);
  }, [reviewsList.length]);

  const activeReview = reviewsList[activeReviewIdx] || reviewsList[0];

  function validate() {
    const errs = {};
    if (!formData.navn.trim()) errs.navn = "Angiv venligst dit navn";
    if (!formData.telefon.trim()) {
      errs.telefon = "Angiv venligst dit telefonnummer";
    } else if (!/^[\d\s+()-]{8,}$/.test(formData.telefon.trim())) {
      errs.telefon = "Angiv et gyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      errs.email = "Angiv venligst din e-mail";
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      errs.email = "Angiv en gyldig e-mailadresse";
    }
    if (!formData.beskrivelse.trim()) {
      errs.beskrivelse = "Skriv venligst hvad opgaven drejer sig om";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Der opstod en fejl under afsendelsen. Prøv venligst igen.");
      }

      setStatus({ submitting: false, success: true, error: null });

      // Google Tag / GTM conversion event
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "generate_lead",
          form_name: "Hero Hurtig Kontakt",
        });
      }

      setFormData({
        navn: "",
        telefon: "",
        email: "",
        beskrivelse: "",
        website_url: "",
      });
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || "Kunne ikke sende formularen. Ring evt. direkte på 42 49 95 95.",
      });
    }
  }

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-7 border border-white/20 relative backdrop-blur-xs">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-stone-900 tracking-tight">
            Få et uforpligtende tilbud
          </h2>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"></span>
            Hurtigt svar
          </span>
        </div>
        <p className="text-stone-500 text-xs mt-1">
          Udfyld formularen, så kontakter vi dig hurtigst muligt.
        </p>
      </div>

      {status.success ? (
        <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto text-xl">
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
          </div>
          <h3 className="text-base font-bold">Mange tak for din henvendelse!</h3>
          <p className="text-xs text-emerald-800">
            Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.
          </p>
          <button
            onClick={() => setStatus({ submitting: false, success: false, error: null })}
            className="px-5 py-2 bg-[#165B4C] hover:bg-[#11463A] text-white text-xs font-semibold rounded-full transition-colors"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-3">
          {/* Honeypot field - ABSOLUTELY REQUIRED & SR-ONLY */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="hero_website_url">Venligst lad dette felt være tomt</label>
            <input
              type="text"
              id="hero_website_url"
              name="website_url"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="hero_navn" className="block text-xs font-semibold text-stone-700 mb-1">
              Navn *
            </label>
            <input
              type="text"
              id="hero_navn"
              value={formData.navn}
              onChange={(e) => {
                setFormData({ ...formData, navn: e.target.value });
                if (errors.navn) setErrors({ ...errors, navn: null });
              }}
              placeholder="F.eks. Lars Hansen"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 bg-stone-50/50 ${
                errors.navn
                  ? "border-rose-400 focus:ring-rose-200 bg-rose-50/30"
                  : "border-stone-200 focus:border-[#165B4C] focus:ring-emerald-100"
              }`}
            />
            {errors.navn && <p className="text-rose-600 text-[11px] mt-1">{errors.navn}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="hero_telefon" className="block text-xs font-semibold text-stone-700 mb-1">
                Telefon *
              </label>
              <input
                type="tel"
                id="hero_telefon"
                value={formData.telefon}
                onChange={(e) => {
                  setFormData({ ...formData, telefon: e.target.value });
                  if (errors.telefon) setErrors({ ...errors, telefon: null });
                }}
                placeholder="F.eks. 12 34 56 78"
                className={`w-full px-3.5 py-2.5 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 bg-stone-50/50 ${
                  errors.telefon
                    ? "border-rose-400 focus:ring-rose-200 bg-rose-50/30"
                    : "border-stone-200 focus:border-[#165B4C] focus:ring-emerald-100"
                }`}
              />
              {errors.telefon && <p className="text-rose-600 text-[11px] mt-1">{errors.telefon}</p>}
            </div>

            <div>
              <label htmlFor="hero_email" className="block text-xs font-semibold text-stone-700 mb-1">
                E-mail *
              </label>
              <input
                type="email"
                id="hero_email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (errors.email) setErrors({ ...errors, email: null });
                }}
                placeholder="din@email.dk"
                className={`w-full px-3.5 py-2.5 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 bg-stone-50/50 ${
                  errors.email
                    ? "border-rose-400 focus:ring-rose-200 bg-rose-50/30"
                    : "border-stone-200 focus:border-[#165B4C] focus:ring-emerald-100"
                }`}
              />
              {errors.email && <p className="text-rose-600 text-[11px] mt-1">{errors.email}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="hero_beskrivelse" className="block text-xs font-semibold text-stone-700 mb-1">
              Besked / Hvad drejer opgaven sig om? *
            </label>
            <textarea
              id="hero_beskrivelse"
              rows={3}
              value={formData.beskrivelse}
              onChange={(e) => {
                setFormData({ ...formData, beskrivelse: e.target.value });
                if (errors.beskrivelse) setErrors({ ...errors, beskrivelse: null });
              }}
              placeholder="Beskriv kort din bolig eller opgave (f.eks. energimærkning af parcelhus i Horsens)..."
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 bg-stone-50/50 resize-none ${
                errors.beskrivelse
                  ? "border-rose-400 focus:ring-rose-200 bg-rose-50/30"
                  : "border-stone-200 focus:border-[#165B4C] focus:ring-emerald-100"
              }`}
            />
            {errors.beskrivelse && (
              <p className="text-rose-600 text-[11px] mt-1">{errors.beskrivelse}</p>
            )}
          </div>

          {status.error && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs">
              {status.error}
            </div>
          )}

          <button
            type="submit"
            disabled={status.submitting}
            className="w-full py-3.5 px-6 rounded-full bg-[#165B4C] hover:bg-[#11463A] disabled:bg-stone-400 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                <span>Sender...</span>
              </>
            ) : (
              <>
                <span>Send henvendelse</span>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true"></i>
              </>
            )}
          </button>
        </form>
      )}

      {/* Link til den store prisberegner */}
      <div className="mt-3.5 pt-3 border-t border-stone-200/80 flex items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 text-stone-600">
          <i className="fa-solid fa-calculator text-[#165B4C]" aria-hidden="true"></i>
          <span className="font-medium">Vil du beregne prisen med det samme?</span>
        </div>
        <Link
          to="/prisberegner"
          className="font-bold text-[#165B4C] hover:text-emerald-800 hover:underline flex items-center gap-1 shrink-0 whitespace-nowrap"
        >
          <span>Gå til prisberegner</span>
          <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
        </Link>
      </div>

      {/* Roterende anmeldelse i forbindelse med formularen */}
      <div className="mt-3.5 bg-stone-50 border border-stone-200/80 p-3 rounded-2xl relative overflow-hidden transition-all duration-300 hover:border-emerald-300 h-[92px] flex flex-col justify-between">
        <div className="flex items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="flex text-amber-400 text-[10px] gap-0.5">
              {[...Array(activeReview.rating || 5)].map((_, i) => (
                <i key={i} className="fa-solid fa-star" aria-hidden="true"></i>
              ))}
            </div>
            <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200/60 px-1.5 py-0.5 rounded-full flex items-center gap-1">
              <i className="fa-solid fa-circle-check text-[9px] text-emerald-600" aria-hidden="true"></i>
              Verificeret kunde
            </span>
          </div>

          <div className="flex items-center gap-1 text-[10px] text-stone-500 font-medium">
            {activeReview.source?.toLowerCase().includes("trustpilot") ? (
              <span className="flex items-center gap-1 text-[#00b67a]">
                <i className="fa-solid fa-star text-[9px]" aria-hidden="true"></i>
                <span className="font-semibold text-stone-600">Trustpilot</span>
              </span>
            ) : (
              <span className="flex items-center gap-1 text-blue-600">
                <i className="fa-brands fa-google text-[9px]" aria-hidden="true"></i>
                <span className="font-semibold text-stone-600">Google Review</span>
              </span>
            )}
          </div>
        </div>

        <div
          className={`transition-opacity duration-300 h-[48px] flex flex-col justify-between overflow-hidden ${
            isFading ? "opacity-0" : "opacity-100"
          }`}
        >
          <p className="font-medium text-stone-800 text-xs line-clamp-2 leading-snug">
            "{activeReview.text}"
          </p>
          <div className="flex items-center justify-between text-[10px] text-stone-500 shrink-0">
            <p className="truncate pr-2">
              {activeReview.author === "Trustpilot-anmelder"
                ? "Trustpilot anmeldelse"
                : activeReview.author}
              {activeReview.location ? `, ${activeReview.location}` : ""}
            </p>
            {activeReview.date && (
              <span className="text-stone-400 shrink-0">{activeReview.date}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
